
import {Component} from "vue-property-decorator";
import {MainLayout} from "@/app";
import ApplicationService from "../service/Application";
import IApplicationForm from "../model/applicationForm/IApplicationForm";
import ApplicationForm from "../model/applicationForm/ApplicationForm";
import {LoginService} from "@/module/user";
import IApplication from "../model/IApplication";
import {ICommunicationError} from "@dev/hc-talk-talk";
import TemplateOptions from "./TemplateOptionsEnum";
import ApplicationFactory from "../model/applicationForm/ApplicationFactory";
import {HcPageModel} from "@dev/hc-components";
import router from "@/app/router";
import RouteName from "@/app/router/NameEnum";
import {notification} from "@/plugins";
import IToken from "@/module/user/dto/IToken";
import Customer from "@/module/main/components/Customer.vue";
import EnvSettings from "@/module/main/components/EnvSettings.vue";
import Loan from "@/module/main/components/Loan.vue";
import FinancedSubjectType from "@/module/main/dto/FinancedSubjectTypeEnum";
import Endpoints from "@/module/main/components/Endpoints.vue";
import ClientType from "@/module/main/dto/ClientTypeEnum";
import ClientTypeEnum from "@/module/main/dto/ClientTypeEnum";
import OrderType from "@/module/main/dto/OrderTypeEnum";
import FinancingType from "@/module/main/dto/FinancingTypeEnum";
import Culture from "@/module/main/page/CultureEnum";
import ParamsDocumentation from "@/app/layouts/ParamsDocumentation.vue";
import QrCode from "@/app/layouts/QrCode.vue";
import CashFinancingType from "@/module/main/dto/CashFinancingTypeEnum";
import YesNo from "@/app/service/YesNoEnum";

@Component({
    components: {
        QrCode,
        ParamsDocumentation,
        EnvSettings,
        Customer,
        MainLayout,
        Loan,
        Endpoints
    }
})
export default class Main extends HcPageModel {
    private formData: IApplicationForm = new ApplicationForm();
    private template: string = "";
    private myLoanURL: string = "";
    protected pageName: string = "application";
    protected showModalQr: boolean = false;

    private get showCustomer(): boolean
    {
        return this.formData.loan.financedSubjectType === FinancedSubjectType.LIABILITY
            || this.formData.loan.financedSubjectType === FinancedSubjectType.ORDER
            || (this.formData.loan.financedSubjectType === FinancedSubjectType.CASH
                && this.formData.loan.clientType === ClientTypeEnum.PERSON)
            || this.formData.loan.financedSubjectType === FinancedSubjectType.WALLET;
    }

    private sendForm(): void {
        this.postProcessData();
        this.$store.dispatch("main/setEnvType", this.formData.envSettings.envType);
        this.$store.dispatch("main/setEnv", this.formData.envSettings.env);
        this.$store.dispatch("main/setEnvBrand", this.formData.envSettings.brand);
        this.$store.dispatch("main/setFinancedSubjectType", this.formData.loan.financedSubjectType);
        this.createInfoNotification("notification-token-request");

        LoginService.login(this.formData.envSettings)
            .then((token: IToken) => {
                this.createSuccessNotification("notification-token-received", token.accessToken);
                this.createApplication();
            })
            .catch((error: ICommunicationError) => {
                this.showError(error);
            });
    }

    private postProcessData(): void {
        this.formData.customer.block1.identificationNumberRaw
            = this.formData.customer.block1.identificationNumber?.replace(/_/g, "");
    }

    private createApplication(): void {
        this.createInfoNotification("notification-create-app-request");
        ApplicationService.createApplication(this.formData)
            .then((application: IApplication) => {
                this.createSuccessNotification("notification-create-app-received", application.id);
                if (!!application.gatewayRedirectUrl) {
                    this.myLoanURL = this.getMyLoanURL(application.gatewayRedirectUrl);
                    if (this.formData.envSettings.copyToClipboard) {
                      navigator.clipboard.writeText(this.myLoanURL)
                          .then(() => {
                            this.createSuccessNotification("notification-entryurl-copied-to-clipboard", this.myLoanURL);
                          })
                          .catch(() => {
                            this.createWarningNotification("notification-entryurl-not-copied-to-clipboard", this.myLoanURL);
                          });
                    }

                    if (this.formData.envSettings.showQrCode) {
                        this.showModalQr = true;
                    } else {
                        this.redirectToMyLoan && this.processRedirectToMyLoan(application.gatewayRedirectUrl);
                    }
                } else {
                    if (this.formData.endpoints.isReturnUrl === "true") {
                        this.createWarningNotification("notification-create-app-no-redirect-wfstep");
                    }
                }
            })
            .catch((error: ICommunicationError) => {
                this.showError(error);
            });
    }

    private get redirectToMyLoan(): boolean {
        let redirectToMyloan: string = this.$route.query["redirectToMyloan"] as string;

        return !(redirectToMyloan === "0" || redirectToMyloan === "false");
    }

    private showError(error: ICommunicationError): void {
        console.log(error);
        this.createErrorNotification("notification-error", error.toString());
    }

    private clearForm(): void {
        this.template = "";
        this.formData = new ApplicationForm();
    }

    private getMyLoanURL(defaultURL: string): string {
        let myLoanURL: string = defaultURL;
        let paramURL: string = this.$route.query["myloanRedirectUrl"] as string;

        if (paramURL) {
            let match = defaultURL.match(/^.*uuid=(.*)$/);
            match && (myLoanURL = paramURL.replace("{0}", match[1]));
        }

        return myLoanURL;
    }

    private setFormData(): void {
        let env: string = this.$store.getters["main/getEnv"];
        if (env === "") {
            this.loadTemplate(TemplateOptions.CZ);
        } else {
            let country: string = env.substring(0, 2);
            let template: TemplateOptions = country === "sk" ? TemplateOptions.SK : TemplateOptions.CZ;
            this.loadTemplate(template);
        }
    }

    private loadTemplate(template: TemplateOptions): void {
        switch (template) {
            case TemplateOptions.CZ:
                this.template = TemplateOptions.CZ;
                this.formData = ApplicationFactory.getTemplate(Culture.CZ);
                break;
            case TemplateOptions.SK:
                this.template = TemplateOptions.SK;
                this.formData = ApplicationFactory.getTemplate(Culture.SK);
                break;
            default:
                alert("Unknown template = " + template);
        }
    }

    private financedSubjectTypeChanged(financedSubjectType: FinancedSubjectType): void
    {
        const clientType = this.$store.getters["main/getClientType"] || ClientType.PERSON;
        const orderType = this.$store.getters["main/getOrderType"] || OrderType.STANDARD;
        const financingType = this.$store.getters["main/getFinancingType"] || FinancingType.INSTALLMENT;
        const cashFinancingType = this.$store.getters["main/getCashFinancingType"] || CashFinancingType.LOAN_WALLET;
        const walletWithOrder = this.$store.getters["main/getWalletWithOrder"] || YesNo.NO;

        this.formData.envSettings.financedSubjectType = financedSubjectType;
        this.formData.envSettings.orderType = orderType;

        this.formData.loan = ApplicationFactory.getLoan(this.formData.culture, financedSubjectType, clientType, orderType,
            financingType, cashFinancingType, walletWithOrder);
        if (this.formData.loan.clientType !== clientType)
            this.$store.dispatch("main/setClientType", this.formData.loan.clientType);

        this.formData.customer = ApplicationFactory.getCustomer(this.formData.culture, financedSubjectType, orderType,
            this.formData.loan.clientType);
    }

    private envSettingsChanged(): void
    {
        const envType = this.$store.getters["main/getEnvType"];
        const env = this.$store.getters["main/getEnv"];
        this.formData.endpoints = ApplicationFactory.getEndpoints(envType, env);
    }

    private processRedirectToMyLoan(gatewayRedirectUrl: string): void {
        let myLoanURL: string = this.getMyLoanURL(gatewayRedirectUrl);
        this.createSuccessNotification("notification-redirect-to-mln");

        this.formData.envSettings.isEmbedded === "false"
            ? (window.location.href = myLoanURL)
            : router.push({name: RouteName.EXTERNAL_SITE_IN_LOKI, params: {url: myLoanURL}});
    }

    private createSuccessNotification(key: string, opt: string = ""): void {
        this.createNotification(key, opt, "success");
    }

    private createErrorNotification(key: string, opt: string = ""): void {
        this.createNotification(key, opt, "error");
    }

    private createWarningNotification(key: string, opt: string = ""): void {
        this.createNotification(key, opt, "warning");
    }

    private createInfoNotification(key: string, opt: string = ""): void {
        this.createNotification(key, opt, "info");
    }

    private createNotification(key: string, opt: string, status: string): void {
        notification.create(this.$localize(key, opt), this.$localize("common-ok"), "", "snackbar", status);
    }

    public created(): void {
        this.setFormData();
    }
}
